import {
  DeviceDesktopIcon,
  DevicePhoneIcon,
  DeviceTabletIcon,
} from '@mint/icons';
import React, { useCallback } from 'react';
import { animated, useSpring } from 'react-spring';
import {
  setSiteViewportSelectorVisible,
  setSiteViewportSize,
} from '../../../actions';
import { useDispatch, useSelector } from 'react-redux';
import { SITE_VIEWPORT_SIZE_PRESETS } from '../../../constants';
import { ViewportSizeIconButton } from './viewport-size-icon-button';
import { getSiteViewportSelectorVisible } from '../../../selectors';
import styled from 'styled-components';

const Wrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  height: 3rem;
  justify-content: center;
  padding-right: 2.5rem;
  position: absolute;
  width: 100%;
  top: 0;
`;

const AnimatedWrapper = animated(Wrapper);

const IconWrapper = styled.div`
  display: inline-block;
  padding: 0.25rem 3rem 0;
  height: 5rem;
`;

const sizes: (keyof typeof SITE_VIEWPORT_SIZE_PRESETS)[] = ['sm', 'md', 'lg'];

const mapSizeToIcon = (
  size: keyof typeof SITE_VIEWPORT_SIZE_PRESETS,
): React.ReactElement => {
  switch (size) {
    case 'sm':
      return <DevicePhoneIcon />;
    case 'md':
      return <DeviceTabletIcon />;
    case 'lg':
      return <DeviceDesktopIcon />;
  }
};

export const SiteViewportPresetSelector: React.FunctionComponent<
  React.HTMLAttributes<HTMLDivElement>
> = ({ style, ...props }): React.ReactElement => {
  const dispatch = useDispatch();
  const isSelectorVisible = useSelector(getSiteViewportSelectorVisible);

  const icons = sizes.map(
    (size): React.ReactElement => {
      const onClick = useCallback((): void => {
        dispatch(setSiteViewportSize(size));
      }, [dispatch]);
      return (
        <ViewportSizeIconButton onClick={onClick} key={size}>
          {mapSizeToIcon(size)}
        </ViewportSizeIconButton>
      );
    },
  );

  const onMouseEnter = useCallback((): void => {
    dispatch(setSiteViewportSelectorVisible());
  }, []);

  const onMouseLeave = useCallback((): void => {
    dispatch(setSiteViewportSelectorVisible(false));
  }, []);

  const { opacity, transform } = useSpring({
    to: {
      opacity: isSelectorVisible ? 1 : 0,
      transform: isSelectorVisible ? 0 : -3.5,
    },
  }) as any;

  return (
    <AnimatedWrapper
      style={{
        ...style,
        opacity: opacity.interpolate((v: number): string => `${v}`),
        transform: transform.interpolate(
          (v: number): string => `translateY(${v}rem)`,
        ),
      }}
      {...props}
    >
      <IconWrapper {...{ onMouseEnter, onMouseLeave }}>{icons}</IconWrapper>
    </AnimatedWrapper>
  );
};
